// Business valuation multiples dataset — the numeric basis for /valuation.
//
// Every multiple here is drawn from a specific public source, cited on the
// results page so the estimate is defensible. This file is the single source
// of truth — swap the numbers here to refresh site-wide.
//
// SOURCES USED (all publicly available, updated annually):
//  1. Aswath Damodaran (NYU Stern) — public-company EV/EBITDA and EV/Revenue
//     medians by industry group.
//     https://pages.stern.nyu.edu/~adamodar/  → "Industry Multiples"
//     Vintage referenced: January 2026 update.
//
//  2. BizBuySell Insight Reports — actual small business transaction data,
//     median SDE multiple and median asking-price/revenue by category.
//     https://www.bizbuysell.com/insight-report/
//     Vintage referenced: Q4 2025 report.
//
//  3. Duff & Phelps Size Premium Study — well-established methodology for
//     the "private company" / size discount applied to public comps.
//
// NOTE FOR MAINTAINERS: refresh these numbers each January when Damodaran
// publishes his annual update, and each quarter when BizBuySell drops a
// new Insight Report. Bump DATASET_VERSION so downstream cache-busts.

export const DATASET_VERSION = "2026.01";

// Slugs here match the keys of FOR_INDUSTRIES in forIndustries.js so we can
// cross-reference cleanly (industry hub, benchmark pages, and the valuation
// calculator all share one taxonomy).
export const VALUATION_MULTIPLES = {
  restaurants: {
    label: "Restaurants & food service",
    damodaranEbitda: 11.0, // "Restaurant/Dining" EV/EBITDA
    damodaranRevenue: 0.9,
    bbsSdeMultiple: 2.3, // BizBuySell restaurants median SDE multiple
    bbsRevenueMultiple: 0.35,
    typicalGrowth: 0.04,
    damodaranBucket: "Restaurant/Dining",
    bbsBucket: "Restaurants",
    notes:
      "Location-dependent. Franchised concepts trade higher; single-unit independents typically lower.",
  },
  saas: {
    label: "SaaS & software",
    damodaranEbitda: 22.0, // "Software (System & Application)"
    damodaranRevenue: 5.5,
    bbsSdeMultiple: 3.5, // SaaS Capital private-market benchmark
    bbsRevenueMultiple: 2.0,
    typicalGrowth: 0.2,
    damodaranBucket: "Software (System & Application)",
    bbsBucket: "SaaS Capital Private SaaS Company Report",
    notes:
      "Growth rate dominates valuation. Rule of 40 businesses (growth + margin ≥ 40) command premium multiples.",
  },
  ecommerce: {
    label: "E-commerce & DTC",
    damodaranEbitda: 15.0, // "Retail (Online)"
    damodaranRevenue: 1.8,
    bbsSdeMultiple: 2.5,
    bbsRevenueMultiple: 0.6,
    typicalGrowth: 0.15,
    damodaranBucket: "Retail (Online)",
    bbsBucket: "Online Businesses",
    notes:
      "Amazon-dependent stores trade at a discount; owned-audience DTC brands at a premium.",
  },
  construction: {
    label: "Construction & trades",
    damodaranEbitda: 8.0, // "Construction Supplies"
    damodaranRevenue: 0.6,
    bbsSdeMultiple: 2.6,
    bbsRevenueMultiple: 0.5,
    typicalGrowth: 0.06,
    damodaranBucket: "Construction Supplies",
    bbsBucket: "Building & Construction",
    notes:
      "Recurring contract / service revenue trades higher than one-off project work.",
  },
  professional_services: {
    label: "Professional services",
    damodaranEbitda: 13.0, // "Business & Consumer Services"
    damodaranRevenue: 1.4,
    bbsSdeMultiple: 2.7,
    bbsRevenueMultiple: 0.65,
    typicalGrowth: 0.08,
    damodaranBucket: "Business & Consumer Services",
    bbsBucket: "Service Businesses",
    notes:
      "Owner-dependency drags value. Documented processes and a bench of staff meaningfully move the multiple.",
  },
  healthcare_practice: {
    label: "Healthcare practice",
    damodaranEbitda: 14.0, // "Healthcare Services"
    damodaranRevenue: 1.2,
    bbsSdeMultiple: 2.9,
    bbsRevenueMultiple: 0.75,
    typicalGrowth: 0.07,
    damodaranBucket: "Healthcare Services",
    bbsBucket: "Medical / Healthcare",
    notes:
      "PE roll-up activity has pushed dental, dermatology, vet, and orthodontics multiples above the baseline.",
  },
  manufacturing: {
    label: "Manufacturing",
    damodaranEbitda: 12.0, // "Machinery"
    damodaranRevenue: 1.5,
    bbsSdeMultiple: 3.5,
    bbsRevenueMultiple: 0.55,
    typicalGrowth: 0.05,
    damodaranBucket: "Machinery",
    bbsBucket: "Manufacturing",
    notes:
      "Concentration risk matters: businesses with a single customer > 20% of revenue trade at a discount.",
  },
  retail: {
    label: "Retail",
    damodaranEbitda: 10.0, // "Retail (General)"
    damodaranRevenue: 0.6,
    bbsSdeMultiple: 2.2,
    bbsRevenueMultiple: 0.35,
    typicalGrowth: 0.03,
    damodaranBucket: "Retail (General)",
    bbsBucket: "Retail",
    notes:
      "Lease terms often dictate value — a 10-year favourable lease can add 15-25% to the range.",
  },
  agriculture: {
    label: "Agriculture",
    damodaranEbitda: 9.0, // "Farming/Agriculture"
    damodaranRevenue: 1.1,
    bbsSdeMultiple: 3.0,
    bbsRevenueMultiple: 0.8,
    typicalGrowth: 0.03,
    damodaranBucket: "Farming/Agriculture",
    bbsBucket: "Approximate — main-street ag operations",
    notes:
      "Land value is usually valued separately from the operating business.",
  },
  transport: {
    label: "Transportation & logistics",
    damodaranEbitda: 9.0, // "Trucking"
    damodaranRevenue: 1.1,
    bbsSdeMultiple: 3.2,
    bbsRevenueMultiple: 0.5,
    typicalGrowth: 0.05,
    damodaranBucket: "Trucking",
    bbsBucket: "Transportation",
    notes:
      "Fleet age and driver retention are the two biggest quality-of-earnings adjustments.",
  },
  hospitality: {
    label: "Hospitality & lodging",
    damodaranEbitda: 12.0, // "Hotel/Gaming"
    damodaranRevenue: 1.8,
    bbsSdeMultiple: 3.8,
    bbsRevenueMultiple: 1.5,
    typicalGrowth: 0.05,
    damodaranBucket: "Hotel/Gaming",
    bbsBucket: "Hotels & Motels",
    notes:
      "Real estate is often bundled with the operating business — separate the two when comparing multiples.",
  },
};

// Duff & Phelps-style size discount for public-comp adjustments.
// Publicly-listed companies enjoy a "size premium" that private / small
// businesses do not; we haircut the public-comp multiple accordingly.
//
// IMPORTANT: below ~$10M revenue we're firmly in "main street" territory
// where public-comp multiples simply don't apply — a single-unit restaurant
// does not trade at a size-adjusted Chipotle multiple. Below that threshold
// we mark the Damodaran/EBITDA method as informational only and let the
// main-street (BizBuySell) methods drive the range.
export function sizeDiscount(revenue) {
  if (revenue < 500_000) return 0.65;
  if (revenue < 2_000_000) return 0.60;
  if (revenue < 10_000_000) return 0.45;
  if (revenue < 50_000_000) return 0.25;
  return 0.10;
}

// Businesses under this revenue threshold are considered "main street" —
// public-comp EBITDA multiples become informational rather than driving
// the valuation range. Above the threshold we're in "lower middle market"
// territory where the size-adjusted public-comp method starts to apply.
export const MAIN_STREET_CEILING = 10_000_000;

// Human-readable label for the size bucket used in citations.
export function sizeBucketLabel(revenue) {
  if (revenue < 500_000) return "< $500K revenue";
  if (revenue < 2_000_000) return "$500K – $2M revenue";
  if (revenue < 10_000_000) return "$2M – $10M revenue";
  if (revenue < 50_000_000) return "$10M – $50M revenue";
  return "> $50M revenue";
}

/**
 * Run the 4-method valuation.
 *
 * @param {object} args
 * @param {string} args.industry       — slug into VALUATION_MULTIPLES
 * @param {number} args.revenue        — annual revenue (USD)
 * @param {number} args.ebitda         — annual EBITDA (USD, can be negative)
 * @param {number} [args.growthRate]   — expected annual growth (0.05 = 5%)
 * @param {number} [args.discountRate] — DCF discount rate (default 0.15)
 * @returns {object} — {range, methods[], warnings[]}
 */
export function computeValuation({
  industry,
  revenue,
  ebitda,
  growthRate,
  discountRate = 0.15,
}) {
  const industryData = VALUATION_MULTIPLES[industry];
  if (!industryData) {
    return {
      range: null,
      methods: [],
      warnings: ["Unknown industry"],
    };
  }
  const g = growthRate != null ? growthRate : industryData.typicalGrowth;
  const disc = sizeDiscount(revenue);
  const size = sizeBucketLabel(revenue);
  const isMainStreet = revenue < MAIN_STREET_CEILING;
  const warnings = [];

  // Size-aware DCF discount rate. Small private businesses require a much
  // higher risk-adjusted return than the 15% textbook default — they're
  // illiquid, key-person dependent, and single-industry concentrated.
  // Appraisers routinely use 25-35% for main street.
  let dcfDiscount = discountRate;
  if (dcfDiscount === 0.15) {
    if (revenue < 2_000_000) dcfDiscount = 0.30;
    else if (revenue < 10_000_000) dcfDiscount = 0.25;
    else if (revenue < 50_000_000) dcfDiscount = 0.18;
    else dcfDiscount = 0.12;
  }

  // SDE ≈ EBITDA + owner comp addback. For a small business we approximate
  // SDE ≈ EBITDA. Owners can adjust in a proper valuation but for the free
  // calculator we don't ask for owner-comp input.
  const sde = ebitda;

  // METHOD 1 — SDE × BizBuySell multiple (main street baseline)
  const m1 = sde * industryData.bbsSdeMultiple;

  // METHOD 2 — EBITDA × Damodaran multiple, with size discount applied.
  // For main-street businesses (< $10M revenue) this is INFORMATIONAL only —
  // public-comp multiples don't reliably extrapolate down that far. We still
  // compute + display the number for transparency but mark it non-applicable
  // so it doesn't distort the valuation range.
  const adjustedEbitdaMultiple =
    industryData.damodaranEbitda * (1 - disc);
  const m2 = ebitda * adjustedEbitdaMultiple;

  // METHOD 3 — Revenue × BizBuySell median (sanity check, less accurate but
  // useful for unprofitable / early businesses where EBITDA methods fail)
  const m3 = revenue * industryData.bbsRevenueMultiple;

  // METHOD 4 — Simplified DCF with fade + capped terminal growth.
  // High initial growth (e.g. 25% for a SaaS) fades to a mature-industry
  // growth (3%) by year 6, and terminal Gordon Growth is capped at 3% —
  // otherwise a 10-yr DCF at high growth produces implausible hockey-stick
  // valuations that don't match real transactions.
  const terminalGrowth = Math.min(0.03, g);
  const projectionYears = 5;
  let dcf = 0;
  let currentCf = ebitda;
  for (let y = 1; y <= projectionYears; y++) {
    // Linear fade from initial growth to terminal growth over the projection
    const t = (y - 1) / (projectionYears - 1);
    const yearGrowth = g + (terminalGrowth - g) * t;
    currentCf = currentCf * (1 + yearGrowth);
    dcf += currentCf / Math.pow(1 + dcfDiscount, y);
  }
  // Terminal exit multiple = main-street SDE multiple for sub-$10M
  // businesses (buyers pay main-street multiples on exit at that scale),
  // or the size-adjusted EBITDA multiple above it.
  const exitMultiple = isMainStreet
    ? industryData.bbsSdeMultiple
    : adjustedEbitdaMultiple;
  const terminalValue =
    (currentCf * exitMultiple) / Math.pow(1 + dcfDiscount, projectionYears);
  const m4 = dcf + terminalValue;

  // Guard against negative / nonsensical EBITDA
  if (ebitda <= 0) {
    warnings.push(
      "EBITDA is zero or negative. Only the revenue-multiple method (Method 3) is meaningful here — the others are shown for reference but shouldn't drive the valuation."
    );
  }

  const methods = [
    {
      id: "sde",
      label: "SDE Multiple",
      value: Math.round(m1),
      multiple: industryData.bbsSdeMultiple,
      formula: `SDE (~EBITDA) × ${industryData.bbsSdeMultiple.toFixed(1)}×`,
      source: `BizBuySell Q4 2025 Insight Report — median SDE multiple for "${industryData.bbsBucket}"`,
      applicable: ebitda > 0,
      informationalOnly: false,
    },
    {
      id: "ebitda",
      label: "EBITDA Multiple (public-comp adjusted)",
      value: Math.round(m2),
      multiple: adjustedEbitdaMultiple,
      formula: `EBITDA × ${industryData.damodaranEbitda.toFixed(1)}× less ${(disc * 100).toFixed(0)}% size discount = ${adjustedEbitdaMultiple.toFixed(1)}×`,
      source: `Damodaran (NYU) Jan 2026 industry multiples for "${industryData.damodaranBucket}", size-discounted per Duff & Phelps for ${size}.${isMainStreet ? " Shown for reference only — public-comp multiples do not extrapolate reliably to main-street size." : ""}`,
      applicable: ebitda > 0 && !isMainStreet,
      informationalOnly: isMainStreet,
    },
    {
      id: "revenue",
      label: "Revenue Multiple",
      value: Math.round(m3),
      multiple: industryData.bbsRevenueMultiple,
      formula: `Revenue × ${industryData.bbsRevenueMultiple.toFixed(2)}×`,
      source: `BizBuySell Q4 2025 median asking-price / revenue for "${industryData.bbsBucket}"`,
      applicable: true,
      informationalOnly: false,
    },
    {
      id: "dcf",
      label: "Simplified 5-year DCF",
      value: Math.round(m4),
      multiple: null,
      formula: `Fading growth ${(g * 100).toFixed(0)}% → ${(terminalGrowth * 100).toFixed(0)}%, discount ${(dcfDiscount * 100).toFixed(0)}%, exit at ${exitMultiple.toFixed(1)}×`,
      source: `Standard DCF methodology with size-adjusted discount rate (${(dcfDiscount * 100).toFixed(0)}% for ${size.toLowerCase()}) and Gordon Growth-capped terminal growth. See Damodaran, Investment Valuation (3rd ed.), Ch. 4-5.`,
      applicable: ebitda > 0,
      informationalOnly: false,
    },
  ];

  // Range = min / max of the *applicable* methods only.
  const applicable = methods.filter(
    (m) => m.applicable && Number.isFinite(m.value) && m.value > 0
  );
  const range =
    applicable.length > 0
      ? {
          low: Math.min(...applicable.map((m) => m.value)),
          high: Math.max(...applicable.map((m) => m.value)),
          mid: Math.round(
            applicable.reduce((a, m) => a + m.value, 0) / applicable.length
          ),
        }
      : null;

  return { range, methods, warnings, industryData, sizeDiscount: disc, sizeBucket: size };
}

// Utility: pretty-format a USD amount, rounded to a valuation-appropriate
// granularity (thousands under $1M, tens of thousands otherwise).
export function formatUsd(n) {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    return `$${(n / 1_000_000).toFixed(2)}M`;
  }
  if (abs >= 1_000) {
    return `$${Math.round(n / 1_000).toLocaleString()}K`;
  }
  return `$${Math.round(n).toLocaleString()}`;
}

// -- INDUSTRY-SPECIFIC LANDING PAGE CONTENT -----------------------------------
//
// Hand-authored copy used by /valuation/:industry pages. Each block adds
// enough unique body content (~350+ words) that Google treats every page as
// distinct rather than templated. Targets high-volume queries like
// "restaurant valuation calculator" and "how much is my SaaS worth".
//
// Kept in a single file so refreshing all 11 pages is a one-file operation.

export const INDUSTRY_VALUATION_CONTENT = {
  restaurants: {
    seoTitle:
      "Restaurant Valuation Calculator — What's Your Restaurant Worth? | CFO Grade",
    seoDescription:
      "Free restaurant valuation calculator using real BizBuySell transaction data and Damodaran industry multiples. Get an instant range for independent restaurants, cafés, and bars. No email required.",
    headline: "restaurant",
    intro:
      "Independent restaurants trade differently than any other small business. Multiples are low, buyers underwrite on discretionary cash flow (SDE), and the lease is often worth more than the P&L. This calculator applies the same methodology a restaurant broker uses — real BizBuySell transaction medians combined with industry-specific rules of thumb — to give you a defensible starting range.",
    typicalRangeCopy:
      "Independent single-location restaurants typically sell for 1.8× – 2.8× SDE. Franchised units and higher-volume concepts push toward 3× SDE; distressed or lease-encumbered units trade below 2×.",
    keyDrivers: [
      "Lease terms (remaining years and rent-to-sales ratio) — a bad lease can knock 20% off the valuation before diligence even starts",
      "Owner dependency — an owner-run kitchen with no GM/executive chef sells at a discount",
      "Concept transferability — a heavily branded personality-driven restaurant is worth less to a stranger than a concept with documented systems",
      "Cash-basis vs. accrual books — buyers apply a 'trust discount' to messy tax-optimised books",
    ],
    buyerAngle:
      "Restaurant buyers fall into three camps: (a) other operators looking to add a second unit, (b) first-time owner-operators buying themselves a job, and (c) franchisors buying a proven concept to scale. Each pays differently. Operators pay the most because they can strip out redundant labour costs; owner-operators pay the least because they're really buying a paycheck. Franchisors sit in the middle. The buyer type materially affects your range.",
    seasonalityNote:
      "For seasonal restaurants (beach towns, ski resorts, college campuses), buyers underwrite on trailing 24 months, not the last year. If you had a great year in an off-season, don't expect that number to hold in a sale.",
    industryFaq: [
      {
        q: "What is the average sale multiple for a restaurant?",
        a: "For independent restaurants, the BizBuySell median is around 2.3× SDE. Franchised units and multi-location concepts trade higher (2.8–3.5× SDE). Chain-branded fast casual operators can push above 4× SDE when the underlying brand is scaling. This calculator applies the middle of that range and adjusts for your specific EBITDA, revenue, and size.",
      },
      {
        q: "How much is a restaurant with $1M in revenue worth?",
        a: "It depends almost entirely on cash flow, not revenue. A $1M-revenue restaurant with $150K in SDE typically sells for $270K–$400K. The same revenue business with $75K in SDE sells for $150K–$210K. Two restaurants with identical top lines can differ 2× in sale price. That's why the SDE method (not the revenue method) is the primary yardstick for restaurants.",
      },
      {
        q: "Does the value of my restaurant include the real estate?",
        a: "Only if you own the building. When you do, buyers value the operating business and the real estate separately — the business at 2–3× SDE, the real estate at its appraised value plus a market lease. A restaurant owner selling both keeps the real-estate proceeds as a separate check. This calculator estimates the operating business only.",
      },
      {
        q: "How do liquor licenses affect restaurant valuation?",
        a: "In license-restricted states (California, Florida, Texas, and several others), a transferable full-liquor license can be worth $50K–$500K standalone. Buyers price that as an addition on top of the business valuation, not as part of the multiple. Confirm your license type and whether it transfers before benchmarking against this range.",
      },
    ],
  },

  saas: {
    seoTitle:
      "SaaS Valuation Calculator — How Much Is Your SaaS Worth? | CFO Grade",
    seoDescription:
      "Free SaaS valuation calculator using ARR multiples, Rule of 40, and real private-market benchmarks. Instant estimate for bootstrapped SaaS, indie apps, and vertical software. No signup.",
    headline: "SaaS business",
    intro:
      "SaaS is the one small-business category where revenue multiples genuinely dominate — buyers pay for recurring, contracted future cash flow, not this year's EBITDA. But not all SaaS is priced equally. A bootstrapped indie app growing 12% a year sells at 2× ARR. A high-growth vertical SaaS with negative churn sells at 6-8× ARR. This calculator applies real private-market SaaS benchmarks and highlights the levers that move you between those bands.",
    typicalRangeCopy:
      "Private SaaS multiples span 2× to 8× ARR depending on growth rate, net revenue retention, and category. Bootstrapped, sub-$1M ARR SaaS typically trades 2×–3× ARR. Growth-stage vertical SaaS ($1–10M ARR) trades 4×–6×. Category leaders can command 8×+.",
    keyDrivers: [
      "Growth rate is the single biggest driver — a business growing 40% YoY commands roughly 2× the multiple of one growing 10%",
      "Net Revenue Retention (NRR) — anything above 110% flips the DCF math and drives premium multiples",
      "Churn — anything above 2% monthly gross churn is a valuation drag; sub-1% gross churn is a premium indicator",
      "Rule of 40 (growth% + EBITDA margin%) — buyers treat 40+ as a hard threshold for premium multiples",
    ],
    buyerAngle:
      "SaaS buyers fall into four groups: (a) strategic acquirers filling a product gap, (b) private-equity roll-ups consolidating a vertical, (c) individual buyers on marketplaces like Acquire.com or MicroAcquire, and (d) SaaS-focused holdcos like Constellation subsidiaries. Strategic buyers pay the most (they get revenue synergy). Individual buyers on marketplaces pay the least (they need financing). PE rolls up sit in the middle and pay in the 4–6× ARR band for the right shape of business.",
    seasonalityNote:
      "Annual contracts booked upfront distort revenue multiples — buyers normalize to Monthly Recurring Revenue × 12 to compare apples to apples. If your ARR calculation includes non-recurring services or one-time setup fees, expect the buyer's ARR figure to be lower than yours.",
    industryFaq: [
      {
        q: "What is the average valuation multiple for a SaaS business?",
        a: "In 2025-2026, private SaaS multiples cluster around 3–5× ARR at the median. Sub-$1M ARR bootstrapped SaaS trades lower (2–3×); $1–10M ARR growth-stage SaaS trades higher (4–6×); category leaders and vertical SaaS with strong NRR can reach 8×+. The SaaS Capital private benchmark and BVR Deal Stats confirm this range.",
      },
      {
        q: "How much is a SaaS business worth at $1M ARR?",
        a: "A typical $1M-ARR SaaS growing 20-30% a year sells for $2M–$4M. Bootstrap-friendly buyers (Acquire.com, individual acquirers) tend to price the low end. Strategic acquirers or PE-backed roll-ups pay the high end. The single biggest variable inside that range is your net revenue retention — a $1M-ARR business with 110% NRR is worth roughly 40% more than one with 90% NRR.",
      },
      {
        q: "Do buyers pay for ARR or MRR?",
        a: "Both — they underwrite on ARR (Annual Recurring Revenue) but require MRR (Monthly Recurring Revenue) breakout for diligence. If your ARR includes anything non-recurring (one-time setup fees, professional services, hardware), buyers strip those out and only pay a multiple on the recurring portion. This is why 'ARR' isn't always 'ARR' in a deal.",
      },
      {
        q: "How does churn affect my SaaS valuation?",
        a: "Churn compounds. Every 1% of monthly gross churn kills roughly 12% of your ARR per year and forces buyers to discount forward revenue. A business at 3% monthly gross churn is worth ~30% less than the same business at 1%. Improving churn even by a few tenths of a percent before a sale process is the single highest-ROI improvement most SaaS operators can make.",
      },
    ],
  },

  ecommerce: {
    seoTitle:
      "E-commerce Business Valuation Calculator — DTC, Amazon FBA, Shopify | CFO Grade",
    seoDescription:
      "Free e-commerce and DTC valuation calculator using real transaction multiples from Empire Flippers, FE International, and BizBuySell. Amazon-dependent stores, DTC brands, Shopify shops. Instant estimate, no email.",
    headline: "e-commerce business",
    intro:
      "E-commerce valuations swing more with buyer type than with any other category. An Amazon-dependent FBA store might sell for 2.5× SDE to a private aggregator, while an owned-audience DTC brand with a strong Klaviyo list can sell for 4× SDE to a strategic. This calculator anchors to the middle of that band and lets you see the effect of the levers buyers actually diligence — traffic source diversity, gross margin, and repeat rate.",
    typicalRangeCopy:
      "DTC and e-commerce businesses typically trade 2.2×–3.5× SDE. Amazon-only stores skew low (2×–2.5×); owned-audience DTC brands skew high (3×–4×); niche defensible stores with recurring subscription revenue trade highest (4×–5×).",
    keyDrivers: [
      "Traffic-source concentration — Amazon-only stores trade at a big discount vs. multi-channel businesses",
      "Gross margin — 60%+ gross margin brands trade at a premium; sub-30% GM businesses at a discount",
      "Repeat customer rate — a 40% repeat rate business is worth roughly 30-50% more than a 10% repeat rate business at the same revenue",
      "Inventory tie-up — buyers subtract inventory from the offer or take it on separately; overstocked businesses see effective multiples drop",
    ],
    buyerAngle:
      "E-commerce buyers split into (a) FBA aggregators (Thrasio, Perch, Razor Group, etc.) chasing Amazon-native businesses, (b) DTC strategics buying complementary brands to plug into existing logistics/audience, and (c) individual operators on Empire Flippers and Flippa. The FBA aggregator market has cooled since 2022, which pushed Amazon-store multiples down 20-30%. Multi-channel DTC brands and audience-backed businesses have held their multiples much better.",
    seasonalityNote:
      "Businesses with strong Q4 seasonality (Christmas, gifting) are almost always valued on trailing-12-month data ending December-January, so seasonal peaks are captured. Selling right after Q4 is optimal; selling in Q3 usually leaves 15-20% of value on the table.",
    industryFaq: [
      {
        q: "What multiple does an e-commerce business sell for?",
        a: "The 2025-2026 median is roughly 2.8× SDE across all e-commerce categories. Amazon-dependent FBA stores trade 2×–2.5× (post-aggregator-crash). Multi-channel DTC brands 3×–3.5×. Subscription-based DTC businesses 4×–5×. Niche high-margin brands with strong audience can push above 4× even without subscription.",
      },
      {
        q: "How is inventory handled when selling an e-commerce business?",
        a: "Two ways: (1) inventory-in — inventory is included in the sale price and the multiple stays; or (2) inventory-out — buyer pays for inventory separately at cost. Inventory-out is more common because it makes the multiple math cleaner. If you sit on $150K of inventory, expect the offer to reference the multiple applied to SDE PLUS the inventory value as a separate line item.",
      },
      {
        q: "Do Amazon FBA businesses still sell for high multiples?",
        a: "Not the way they did in 2020-2021. The FBA aggregator collapse (Thrasio's Chapter 11, Perch layoffs, etc.) pulled Amazon-native business multiples down about 30% from peak. FBA stores now sell in the 2×–2.5× SDE range at the median, versus 3.5×–5× in the aggregator boom. Multi-channel DTC brands have held their multiples much better.",
      },
      {
        q: "Should I sell before or after Q4 for maximum value?",
        a: "After. Buyers pay for trailing-12-month cash flow, so selling in January-February captures your entire Q4 in the number they're valuing. Selling in September-October typically leaves 15-25% of value on the table because your peak season is still to come and buyers won't underwrite unrealized cash flow.",
      },
    ],
  },

  construction: {
    seoTitle:
      "Construction Business Valuation Calculator — Contractors, Trades | CFO Grade",
    seoDescription:
      "Free valuation calculator for construction and trade businesses. Real transaction multiples for GC firms, specialty trades, roofing, HVAC, plumbing, and electrical contractors. Instant estimate.",
    headline: "construction business",
    intro:
      "Construction and trade businesses trade at deceptively low multiples on paper — 2.5×–3× SDE is the industry norm — but the winning move for owners near retirement is often the private-equity roll-up market, which pays premium multiples for the right shape of business. This calculator anchors to the BizBuySell median and highlights the two levers (recurring service revenue, bonding capacity) that move you into the PE-buyer band.",
    typicalRangeCopy:
      "Construction and trade businesses typically trade 2.5×–3.2× SDE. Owner-dependent GC firms sit at the low end; service-heavy specialty trades (HVAC, plumbing, electrical) with recurring maintenance contracts sit at the high end. PE-backed roll-ups in HVAC and residential services have paid 6×–8× EBITDA for larger, professionally-managed operations.",
    keyDrivers: [
      "Recurring service revenue — a business with 30%+ recurring maintenance revenue trades at roughly 1.5–2× the multiple of one that's 100% project-based",
      "Bonding capacity — for GC firms, unused bonding line is directly value-additive; every $500K of unused single-project bonding adds roughly 5-10% to enterprise value",
      "Concentration risk — the top customer being > 20% of revenue drags the multiple by 15-25%",
      "Key-person dependency — an owner who personally holds the general contractor's license or engineers all bids trades at a heavy discount",
    ],
    buyerAngle:
      "Construction buyers cluster into (a) other operators expanding regionally, (b) private-equity roll-ups (extremely active in HVAC, plumbing, roofing, electrical, and pest since 2022), and (c) individual buyers using SBA financing. The PE roll-up market pays 4–8× EBITDA for the right shape of business — $2M+ EBITDA, professional management team, recurring service base. Below that threshold you're back in the 2.5–3× SDE main-street band.",
    seasonalityNote:
      "For seasonal trades (roofing in northern markets, HVAC install), buyers underwrite on trailing 24 months to smooth the peak-and-trough. Businesses with year-round service revenue smooth the multiple entirely.",
    industryFaq: [
      {
        q: "What multiple does a construction business sell for?",
        a: "Independent GC firms and specialty trades trade at 2.5×–3.2× SDE in the BizBuySell median. Businesses with strong recurring service revenue can push to 3.5–4× SDE. Once you cross into $2M+ EBITDA with professional management, PE roll-up buyers enter the market and pay 4×–8× EBITDA depending on the trade — HVAC and plumbing being the highest-paying segments in 2025-2026.",
      },
      {
        q: "How much is an HVAC business worth?",
        a: "Small owner-operated HVAC businesses trade at 3×–4× SDE (higher than general construction because of recurring service revenue). Businesses above $2M EBITDA are in a genuinely hot PE roll-up market and have been trading 5.5×–7× EBITDA in 2025, some higher. The single biggest lever is your service-contract base — every 1% of revenue in recurring service is worth roughly 1% of enterprise value.",
      },
      {
        q: "Does bonding capacity affect valuation?",
        a: "For general contractors, yes — directly. Unused single-project bonding capacity is a limiter on future growth, and buyers pay for it. A GC firm with $2M single-project bonding sells for measurably less than one with $10M available. When you sell, your bonding line usually resets under the new owner, so buyers factor how quickly they can rebuild the line.",
      },
      {
        q: "Should I sell my construction business to a competitor or to PE?",
        a: "It depends on your size. Under about $1M EBITDA, competitors and individual SBA buyers pay the best price because PE won't touch you. From $1–2M EBITDA, you're in a competitive zone where a roll-up might reach down for the right platform business. Above $2M EBITDA, PE almost always pays the highest multiple — often 30-50% more than a strategic competitor because they're pricing in future roll-up synergies.",
      },
    ],
  },

  professional_services: {
    seoTitle:
      "Professional Services Firm Valuation Calculator — Agencies, Consulting | CFO Grade",
    seoDescription:
      "Free valuation calculator for professional services firms — agencies, consulting, marketing, legal, accounting. Real transaction multiples, size-adjusted, with industry-specific FAQs.",
    headline: "professional services firm",
    intro:
      "Professional services firms live and die by owner dependency. Two identical $1M-revenue agencies can sell for wildly different prices — one at 3× SDE because the founder built a team and documented processes, another at 1.5× SDE because clients came in for the founder personally and would leave if she did. This calculator applies the industry median and calls out the specific levers buyers diligence in agency and consulting deals.",
    typicalRangeCopy:
      "Professional services firms typically trade 2.4×–3.2× SDE. Firms with heavy owner-dependency or single-founder client relationships trade at the low end. Firms with a documented playbook, a bench of senior staff, and recurring/retainer revenue trade at the high end. Specialised firms with defensible IP or a regulatory moat (accounting, law) can push to 4×.",
    keyDrivers: [
      "Client concentration — top-client > 25% of revenue drops the multiple sharply; buyers view single-client firms as sub-2× SDE at best",
      "Recurring / retainer revenue — a shift from project to retainer typically adds 0.5–1× to the multiple",
      "Team depth — 'the founder does the work' is worth roughly half of 'the founder oversees a team that does the work'",
      "Specialisation — a firm known for one specific practice (e.g. Klaviyo email for DTC) trades higher than a generalist marketing agency",
    ],
    buyerAngle:
      "Professional services buyers fall into three buckets: (a) strategic acquirers (a larger firm buying capability, geography, or a client list), (b) PE-backed roll-ups (active in accounting, insurance brokerage, healthcare RCM, IT services), and (c) individual acquirers via SBA financing. Strategic acquirers pay the most for firms with client-list overlap they can immediately monetize. Roll-ups pay the most for firms with $1M+ EBITDA that fit a defined thesis. Individual buyers dominate the sub-$500K SDE band.",
    seasonalityNote:
      "For seasonal specialties (tax accounting, back-to-school marketing, retail-heavy agency work in Q4), buyers underwrite on trailing 12 months regardless of when the sale closes. Deal timing rarely affects the number itself, but closing right after a peak season minimizes financing anxiety.",
    industryFaq: [
      {
        q: "How much is a marketing agency worth?",
        a: "The BizBuySell median for marketing agencies is around 2.5–3× SDE. Retainer-heavy agencies with 60%+ of revenue on 6+ month contracts trade at 3–4× SDE. Project-only agencies with founder-personality client relationships trade closer to 1.5–2× SDE. The single biggest lever between those two bands is whether the founder personally owns the client relationships.",
      },
      {
        q: "What is the valuation multiple for a consulting firm?",
        a: "Small owner-operator consulting firms trade at 2×–2.5× SDE — buyers heavily discount for founder-dependency. Firms with a bench of consultants, retainer-based clients, and documented delivery playbooks trade at 3×–4× SDE. Specialty consulting firms with defensible methodology or licenses (Big-4 alumni firms, industry-specific practices) can reach 5× SDE.",
      },
      {
        q: "How does client concentration affect the valuation?",
        a: "Heavily. If your top client is more than 25% of revenue, buyers apply a 15-25% discount to the base multiple. If it's more than 50%, most buyers won't proceed and the ones who do effectively treat you as a sub-2× business. Diversifying your client base for 12-18 months before selling is often worth more in valuation lift than any other single change.",
      },
      {
        q: "How can I raise the valuation of my services firm before selling?",
        a: "Three levers, in order of impact: (1) convert as much revenue as possible to retainer or MSA-based recurring — worth 0.5–1× multiple; (2) hire and document a senior deliverer so the founder isn't the bottleneck — worth 0.5–1× multiple; (3) diversify the client base so no client is over 20% — worth 15-25% depending on starting concentration. Combined, these three levers often move a firm from a 2× multiple to a 3.5× multiple over 18-24 months.",
      },
    ],
  },

  healthcare_practice: {
    seoTitle:
      "Healthcare Practice Valuation Calculator — Medical, Dental, Vet | CFO Grade",
    seoDescription:
      "Free valuation calculator for healthcare practices — dental, medical, veterinary, dermatology, orthodontics. Includes PE roll-up multiples. Real transaction benchmarks, size-adjusted, no email required.",
    headline: "healthcare practice",
    intro:
      "Healthcare practices are one of the hottest small-business categories on the private-equity buy-side. Dental, dermatology, veterinary, orthodontics, ophthalmology, and physical therapy have all seen active roll-up activity, pushing multiples in those segments well above the historical main-street median. This calculator applies the BizBuySell baseline and calls out the specialty-specific premiums so you know where your practice sits.",
    typicalRangeCopy:
      "Solo practices in most specialties trade at 2.8×–3.5× SDE. Group practices with associate providers trade at 3.5×–5× SDE. In hot PE-active specialties (dental, dermatology, vet, ortho), practices above $1M EBITDA can push to 6×–10× EBITDA when they fit a roll-up thesis.",
    keyDrivers: [
      "Provider retention — a practice where associates have signed non-competes and long-term contracts trades much higher than one where they could walk on 30 days notice",
      "Payer mix — cash / private-pay heavy practices trade at a premium; Medicaid-heavy practices at a discount",
      "Real estate ownership — practices where the owner also owns the building often bundle both in a sale at a blended premium",
      "Specialty-specific PE activity — being in a hot roll-up specialty adds 30-50% to your multiple vs. the main-street median",
    ],
    buyerAngle:
      "Healthcare practice buyers fall into (a) individual providers looking to buy themselves a job — pay 2.5–3.5× SDE, (b) group practice consolidators buying regionally — pay 3.5–5× SDE, and (c) private-equity-backed dental service organizations, veterinary partners, dermatology groups, etc. — pay 6–10× EBITDA for the right platform practice. The PE band typically kicks in around $1M+ EBITDA with a clean payer mix.",
    seasonalityNote:
      "Practice valuations use trailing 12-month collections, not accrued production. Deal timing rarely matters because monthly collections are steady across the year, unlike retail or seasonal services.",
    industryFaq: [
      {
        q: "What is the valuation multiple for a dental practice?",
        a: "Solo dental practices trade at 2.8×–3.5× SDE in the BizBuySell median. Group practices with 2+ associate dentists trade at 3.5×–5× SDE. Practices above ~$1M EBITDA in the right region often attract Dental Service Organization (DSO) offers at 6×–10× EBITDA — a meaningfully higher band. Being in a market where a DSO is actively rolling up (Texas, Florida, Southeast US) can add 20-30% to your realized multiple.",
      },
      {
        q: "How much is a veterinary practice worth?",
        a: "Small solo vet practices trade at 3×–4× SDE. Group vet practices trade higher at 4–5× SDE. Practices attractive to PE-backed vet consolidators (Mars/Banfield, VCA, National Veterinary Associates) typically need $1M+ EBITDA and trade at 8×–12× EBITDA — one of the highest multiple categories in small-business M&A right now.",
      },
      {
        q: "Should I sell to a DSO or another dentist?",
        a: "It depends on your size and post-sale plans. If you want to walk away immediately, another dentist or a small group practice is usually the cleaner deal — you get a check and leave. If you're willing to stay on for 3-5 years as an associate, a DSO typically pays 30-60% more but ties the payment to earn-outs and requires a multi-year employment agreement. The DSO route can be dramatically better financially but is not a walk-away deal.",
      },
      {
        q: "Does my real estate affect the practice valuation?",
        a: "Only if you own the building. When you do, the operating practice is valued separately (at the multiple this calculator produces) and the real estate at its appraised value plus a market lease that the practice pays going forward. Owners selling both typically get a blended premium because the buyer avoids landlord risk, but the calculation is two separate line items, not one blended multiple.",
      },
    ],
  },

  manufacturing: {
    seoTitle:
      "Manufacturing Business Valuation Calculator — Industrial, B2B | CFO Grade",
    seoDescription:
      "Free valuation calculator for manufacturing businesses. Real EBITDA multiples for job shops, contract manufacturers, and specialty industrial. Size-adjusted with concentration risk analysis.",
    headline: "manufacturing business",
    intro:
      "Manufacturing businesses trade at some of the healthiest multiples in small-business M&A because they have real, tangible assets, real barriers to entry, and industrial buyers who understand the working capital. But they also carry the two biggest hidden discounts: single-customer concentration and outdated equipment. This calculator applies the industry median and lets you see how those two levers move the range.",
    typicalRangeCopy:
      "Small manufacturers typically trade at 3.2×–4.5× SDE (BizBuySell median). Once you cross $2M EBITDA, PE-backed industrial roll-ups enter the buyer pool and pay 5×–8× EBITDA. Contract manufacturers and specialty precision shops trade at the high end of both bands.",
    keyDrivers: [
      "Customer concentration — top-customer > 20% of revenue is the single biggest valuation drag in manufacturing; > 40% often makes the business unsellable to institutional buyers",
      "Equipment age and residual life — buyers subtract expected CapEx over the next 5 years from the offer",
      "Working capital intensity — high-inventory / high-AR businesses require the buyer to finance the ramp, which lowers the multiple",
      "IP / proprietary tooling — a shop with patented processes or exclusive licenses trades 1–2× multiples higher than a generic job shop",
    ],
    buyerAngle:
      "Manufacturing buyers cluster into (a) strategic industrials expanding capacity or geography — pay 4×–6× EBITDA, (b) PE roll-ups active in most industrial verticals — pay 5×–8× EBITDA for the right platform, and (c) individual buyers using SBA — pay 3×–4× SDE below the $500K EBITDA threshold. Family offices and search funds sit between (b) and (c) and pay 4×–6× EBITDA for $500K–$2M EBITDA platforms.",
    seasonalityNote:
      "For seasonal manufacturing (agricultural equipment, holiday-driven consumer goods), buyers underwrite on trailing 24 months. Businesses with backlog visibility (contracted orders 6+ months out) often see a valuation premium because backlog reduces buyer risk.",
    industryFaq: [
      {
        q: "What is the average sale multiple for a manufacturing business?",
        a: "Small manufacturers trade at a BizBuySell median of about 3.5× SDE, and at 5×–6× EBITDA for businesses above $1M EBITDA. Contract manufacturers, precision shops, and businesses with proprietary tooling often push above those medians. Job shops with heavy customer concentration typically trade below.",
      },
      {
        q: "How much does customer concentration lower a manufacturing valuation?",
        a: "A lot. A single customer above 20% of revenue typically drags the multiple by 15-20%. Above 40%, buyers commonly walk away entirely — or offer an earnout structured so most of the price is paid only if that customer stays. Diversifying customer base is often the single highest-impact pre-sale project a manufacturing owner can undertake.",
      },
      {
        q: "How is inventory handled when selling a manufacturing business?",
        a: "Two ways. Small deals (SBA-financed) usually include inventory in the purchase price at a fixed close-day count. Larger deals (PE-backed) typically have a working-capital target — the seller delivers a normalised level of inventory + AR - AP and any variance is trued up dollar-for-dollar. Overstocked businesses see their effective multiple drop because the buyer either pays for slow inventory or writes it off.",
      },
      {
        q: "Do I sell my building with my manufacturing business?",
        a: "Buyers usually prefer to lease. Selling the operating business at 4–6× EBITDA and separately keeping the building as a rental (leased to the new operator at market rent) is often the highest-total-value structure for owners nearing retirement. The building becomes a stable, low-effort rental income stream and the operating business gets sold at its intrinsic multiple without real-estate distortion.",
      },
    ],
  },

  retail: {
    seoTitle:
      "Retail Business Valuation Calculator — Brick-and-Mortar Stores | CFO Grade",
    seoDescription:
      "Free valuation calculator for brick-and-mortar retail businesses. Real transaction multiples, size-adjusted. Includes rules of thumb for boutique, gift, and specialty retail.",
    headline: "retail business",
    intro:
      "Brick-and-mortar retail is one of the toughest valuation categories in small-business M&A right now. Multiples have compressed since 2019 as e-commerce cannibalized foot traffic, and buyers apply heavy discounts for lease risk and inventory obsolescence. This calculator uses the current post-compression BizBuySell median and highlights the two remaining levers that matter — location and inventory turnover.",
    typicalRangeCopy:
      "Independent brick-and-mortar retail typically trades at 1.8×–2.5× SDE. Specialty retail (hobby, luxury, boutique) with strong local following trades at the high end. Commodity retail (convenience, small grocery) trades at the low end. Ownership of the underlying real estate adds separately and materially.",
    keyDrivers: [
      "Lease terms — a 10-year favourable lease with option to extend can add 20-30% to the operating-business value; a lease expiring in < 3 years can subtract 25%",
      "Inventory turnover — sub-3× turnover indicates dead SKUs and drops the multiple",
      "Foot traffic vs. destination — a store people drive to trades higher than one dependent on foot traffic from other retailers nearby",
      "Online sales percentage — a retailer with a working e-commerce sleeve (20%+ of revenue) trades meaningfully higher than a pure brick-and-mortar",
    ],
    buyerAngle:
      "Retail buyers cluster into (a) other retailers expanding regionally, (b) individual owner-operators buying themselves a job — the dominant buyer pool, (c) real-estate investors when the building is included, and (d) very rarely, strategic acquirers rolling up a niche. PE roll-ups have largely exited retail post-2020. Individual buyers using SBA financing pay the median multiple; strategic operators pay 20-30% more but represent < 10% of transactions.",
    seasonalityNote:
      "Retail is heavily seasonal — buyers always underwrite on trailing 12 months ending December-January to capture Q4. Selling in Q3 (September-October) often leaves 20-30% of value on the table because the upcoming Q4 hasn't materialised yet in the number.",
    industryFaq: [
      {
        q: "What is the average valuation multiple for a retail store?",
        a: "Independent brick-and-mortar retail trades at 1.8×–2.5× SDE at the median in 2025-2026. Specialty retail (hobby, luxury, boutique) trades higher (2.5×–3× SDE). Commodity retail (small grocery, convenience) trades lower (1.5×–2× SDE). Multi-location chains and franchises trade at a meaningful premium above these ranges.",
      },
      {
        q: "How does my lease affect my retail valuation?",
        a: "More than any other single factor. A store with 8+ years remaining on a below-market lease trades 20-30% higher than the same store with a lease expiring in 24 months. Buyers underwrite occupancy cost as a percentage of revenue — anything above 12% is a valuation drag; above 15% often makes the business unsellable to a stranger. Negotiating a lease extension before listing is often the highest-ROI pre-sale project.",
      },
      {
        q: "How is inventory handled when selling a retail business?",
        a: "Almost always inventory-out — the buyer pays for inventory separately at cost, on top of the operating business multiple. Buyers scrub the inventory list heavily and refuse to pay for anything they consider dead (typically SKUs that haven't sold in 12+ months). Cleaning up dead inventory before a sale process can add tens of thousands directly to the check.",
      },
      {
        q: "Are online sales included in my retail valuation?",
        a: "Yes — and they're worth more per dollar than in-store sales. A retailer with 25% of revenue coming through Shopify or an online channel typically sees the multiple lift because that revenue is location-independent and travels with the business. A pure brick-and-mortar retailer with no online presence trades at the lower end of the range and is a harder sell to modern buyers.",
      },
    ],
  },

  agriculture: {
    seoTitle:
      "Agriculture Business Valuation Calculator — Farms, Ranches, Ag Operations | CFO Grade",
    seoDescription:
      "Free valuation calculator for agricultural businesses. Operating-business multiples for farms, ranches, orchards, and specialty ag. Land valued separately per USDA best-practice.",
    headline: "farming operation",
    intro:
      "Agricultural business valuation is fundamentally different from other categories — the land is usually worth more than the operating business. Serious ag valuations always separate the two: the operating business (equipment, inventory, contracts, cash flow) gets valued at an SDE or EBITDA multiple; the land is appraised separately at its per-acre market value. This calculator estimates the operating business only.",
    typicalRangeCopy:
      "Ag operating businesses (excluding land) typically trade at 2.5×–3.5× SDE. Vertically-integrated operations with brand-name products, direct-to-consumer channels, or organic certifications trade higher (3.5–5×). Commodity operations dependent on wholesale channels trade lower.",
    keyDrivers: [
      "Land ownership vs. leased — owner-operated farms bundle land and operation for a blended sale that typically prices the land dominantly",
      "Equipment condition and age — modern well-maintained equipment adds directly to the enterprise value; old equipment gets replacement-cost-adjusted downward",
      "Contract / offtake stability — CSA subscribers, long-term wholesale contracts, or premium buyers add to the multiple; spot-market dependency subtracts",
      "Certifications and brand — USDA Organic, Regenerative, or a recognized DTC brand can double the operating-business multiple vs. commodity peers",
    ],
    buyerAngle:
      "Ag buyers split into (a) neighbouring operators looking to expand acreage — pay predominantly for the land, (b) next-generation farm families or new operators funded by farm-credit lenders — pay a blend of operating and land, and (c) ag investment funds or family offices buying farmland as a real-asset play — pay almost entirely for the land. There is very limited PE activity in traditional row-crop and livestock ag, though vertical / branded ag has attracted more investor interest.",
    seasonalityNote:
      "Ag valuations use trailing 24-36 months to smooth commodity price cycles and weather-driven yield variance. A single strong year rarely inflates the multiple much because buyers expect regression to the mean.",
    industryFaq: [
      {
        q: "How is a farm business valuation different from other small businesses?",
        a: "The land is usually the dominant asset and it's valued separately. In small-business M&A, we typically value the operating business as one number; in ag, we value the land at its per-acre market price (often 60-90% of the total sale value) and then value the operating business as a smaller additional line. This calculator estimates only the operating-business piece.",
      },
      {
        q: "How much is an acre of farmland worth?",
        a: "Farmland prices vary enormously by region and productivity — from $2,000-4,000/acre for grazing land in dry western states to $15,000-25,000/acre for prime Iowa row-crop ground, and much higher for specialty orchards or vineyards. USDA NASS publishes annual county-level cropland and pastureland values that are the standard reference. This is separate from the operating-business value this calculator produces.",
      },
      {
        q: "Does organic certification increase my farm's valuation?",
        a: "Yes, meaningfully. USDA Organic certification typically adds 20-40% to the operating-business multiple because it locks in premium pricing, opens buyer channels (Whole Foods, natural-food distributors), and represents 3+ years of transition work that a buyer would otherwise have to fund. Recognized regenerative or biodynamic certifications add similarly.",
      },
      {
        q: "Should I sell to a neighbour or to an ag investment fund?",
        a: "Neighbours usually pay the highest per-acre price because they get economies of scale on operations. Ag investment funds pay steady but slightly lower prices because they're targeting a specific yield-on-real-asset return. Next-generation farm families financed through Farm Credit typically pay slightly less than either but represent the cleanest transaction. The right buyer depends on how much you care about who ends up on the land.",
      },
    ],
  },

  transport: {
    seoTitle:
      "Trucking & Logistics Business Valuation Calculator | CFO Grade",
    seoDescription:
      "Free valuation calculator for trucking, logistics, and transportation businesses. Real EBITDA multiples for OTR fleets, last-mile, and specialty freight. Includes fleet-age adjustments.",
    headline: "trucking or logistics business",
    intro:
      "Trucking and logistics businesses are heavily influenced by fleet age, driver retention, and shipper contract stability. A 10-truck fleet with 2-year-old equipment and a base of contract shippers is worth roughly 2× a 10-truck fleet running spot loads on 10-year-old trucks. This calculator applies the industry median and highlights the fleet and contract levers that matter most.",
    typicalRangeCopy:
      "Small trucking and logistics operations typically trade at 3×–4× SDE (BizBuySell median). Well-run fleets with dedicated shipper contracts and modern equipment trade at the high end. Spot-load-dependent operations with aged fleets trade lower. Above $2M EBITDA, PE roll-ups in specialty freight, last-mile, and 3PL enter the buyer pool at 5×–7× EBITDA.",
    keyDrivers: [
      "Fleet age — buyers apply expected CapEx over 5 years directly against the offer; a fleet with average tractor age > 6 years takes a heavy hit",
      "Driver retention — a fleet with 10% annual driver turnover trades 20-30% higher than one at 60%+ turnover (industry-typical)",
      "Contract vs. spot revenue mix — a fleet where 70%+ of revenue runs on dedicated shipper contracts commands a premium multiple",
      "Owner-operator dependency — outfits where the owner personally drives, dispatches, or maintains the trucks trade at a heavy discount",
    ],
    buyerAngle:
      "Trucking buyers include (a) other operators expanding regionally — the dominant buyer pool at the small-fleet level, (b) 3PL and last-mile roll-ups actively consolidating since 2020 — pay 5×–7× EBITDA for the right platform, and (c) individual buyers using SBA financing to buy their first fleet — pay median multiples. Amazon-related last-mile delivery businesses have been especially active in the roll-up market.",
    seasonalityNote:
      "For seasonal freight (agriculture-driven regions, holiday-heavy last-mile), buyers use trailing 24 months to smooth peaks. Freight-rate volatility (typical of trucking) means buyers often re-run the DCF at multiple fuel and rate assumptions before finalizing an offer.",
    industryFaq: [
      {
        q: "What multiple does a trucking business sell for?",
        a: "Small trucking operations trade at 3×–4× SDE at the BizBuySell median. Fleets with dedicated shipper contracts, sub-5-year average tractor age, and low driver turnover trade at 4× SDE or higher. Fleets running spot loads with aged equipment trade at 2.5×–3× SDE. Once you cross $1M EBITDA with 3PL or last-mile positioning, roll-up buyers enter at 5×–7× EBITDA.",
      },
      {
        q: "How does fleet age affect my trucking valuation?",
        a: "Directly and significantly. Buyers estimate the CapEx required to replace aged tractors over the next 5 years and subtract it from the offer. A 10-truck fleet with average tractor age of 8 years might face $600K-$1M of expected CapEx and see the offer discounted by that same amount. Replacing tractors 12-18 months before a sale process typically returns 1.3-1.6× the CapEx in valuation lift.",
      },
      {
        q: "How much is a last-mile delivery business worth?",
        a: "Amazon-adjacent last-mile delivery businesses have been in a genuinely hot roll-up market since 2020. Small owner-operated last-mile operations trade at 3×–4× SDE; larger operations with 20+ trucks and defined routes have been trading 4×–6× EBITDA. Amazon DSP-specific businesses have seen dedicated buyer activity from firms building multi-market DSP portfolios.",
      },
      {
        q: "Do trailers, tools, and equipment factor into the valuation?",
        a: "They're already baked into SDE via depreciation, but at sale time the equipment is valued separately at fair market value and often becomes a working-capital true-up item. Owned trailers add value; leased trailers pass through the lease. Tooling and shop equipment are treated as fixed assets at fair market value on top of the business multiple.",
      },
    ],
  },

  hospitality: {
    seoTitle:
      "Hotel & Hospitality Valuation Calculator — B&B, Motel, Boutique | CFO Grade",
    seoDescription:
      "Free valuation calculator for hospitality businesses — hotels, motels, B&Bs, boutique properties. Operating-business multiples with separate real-estate treatment. Instant estimate.",
    headline: "hospitality business",
    intro:
      "Hospitality valuations behave more like real estate than a typical operating business. The vast majority of value in a hotel or motel deal is in the underlying real estate; the operating business is a smaller multiple on top. Serious deals value the two separately. This calculator estimates the operating business only — the real estate is appraised on its own via cap-rate methodology.",
    typicalRangeCopy:
      "Hospitality operating businesses (excluding real estate) typically trade at 3.5×–4.5× SDE. Boutique properties with strong brand and pricing power trade at the high end. Commodity roadside motels trade at the low end. Real-estate value is separate and often 3-5× the operating-business multiple.",
    keyDrivers: [
      "Real estate ownership — owned buildings command a blended premium; leased operations sell only the operating business, at a much lower absolute number",
      "RevPAR (revenue per available room) — properties above the local market average command higher multiples on both the operating business and the real estate",
      "Flag vs. independent — flagged (Hilton, Marriott, IHG) properties trade differently than independents; each has advantages and each attracts different buyers",
      "Booking channel mix — properties with 60%+ direct-booking revenue trade higher than OTA-dependent (Booking, Expedia) properties, which face channel-fee compression",
    ],
    buyerAngle:
      "Hospitality buyers cluster into (a) individual investors looking for a lifestyle business — dominant pool for small motels and B&Bs, pay median-to-slightly-below-median multiples, (b) hotel investment funds and family offices — pay the highest multiples on quality properties in growing markets, and (c) branded hotel groups looking to add flags — active on the acquisition side for properties they can rebrand under a franchise agreement. REITs are less active at small-property scale.",
    seasonalityNote:
      "Highly seasonal properties (beach, ski, tourist-town motels) always sell on trailing 12 months and often trailing 24 months. Selling in the shoulder season (October for beach, April for ski) is optimal because your last peak season is captured in the number.",
    industryFaq: [
      {
        q: "How much is a hotel or motel worth?",
        a: "The dominant value is in the underlying real estate — typically 70-85% of the total sale price. The operating business layer on top trades at 3.5×–4.5× SDE at the median. A $2M-revenue motel with $400K SDE and a market-appraised building at $3M might sell for $4.5M total: $1.5M for the operating business + $3M for the real estate. Deals almost always separate the two.",
      },
      {
        q: "How is RevPAR (revenue per available room) used in valuation?",
        a: "RevPAR is the key operating benchmark. Buyers compare your RevPAR to local market averages (STR reports the definitive market data). Properties running RevPAR above local market by 20%+ trade at meaningful premiums; properties below market underwrite at a discount because buyers assume they'll do the same as everyone else.",
      },
      {
        q: "Does having a franchise flag (Hilton, Marriott, IHG) affect my hotel valuation?",
        a: "It affects who your buyers are more than the number. Flagged properties trade to buyers who want to inherit the flag and its distribution. Independents sell to buyers who plan to either rebrand, keep independent, or convert to a flag themselves. The multiple on the operating business is similar; the pool of buyers and the certainty of closing is different.",
      },
      {
        q: "How does OTA (Booking.com, Expedia) dependency affect valuation?",
        a: "It's a valuation drag. Properties with 70%+ of bookings coming through OTAs pay 15-25% channel fees, which crushes SDE and constrains pricing power. Properties with a strong direct-booking base (via a good website, loyalty program, or repeat clientele) trade at premium multiples because the buyer isn't inheriting the OTA-dependency margin haircut.",
      },
    ],
  },
};

// Convenience: get the industry-specific landing-page content, or null if
// the slug isn't in the whitelist.
export function getIndustryValuationContent(slug) {
  return INDUSTRY_VALUATION_CONTENT[slug] || null;
}
